import { lazy, Suspense, useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Menu, X, Home, Search, ChevronDown } from "lucide-react";
// Search index (797 guides + calculators) loads only on first open.
const GlobalSearch = lazy(() => import("@/components/GlobalSearch"));
import StatePickerDialog from "@/components/StatePickerDialog";
import { CALCULATOR_LIST } from "@/lib/calculators";
import { RESEARCH_CLUSTERS, STATE_OVERVIEW_LINK, FREE_TOOLS, PILLAR_GUIDES } from "@/lib/research-clusters";

const otherLinks = [
  { to: "/guides", label: "Guides" },
  // 2026-02 — Surfaced to top nav so first-time visitors can find the
  // 13-matchup ROI head-to-head cluster without scrolling the homepage.
  { to: "/roi-showdown", label: "ROI Showdown" },
  { to: "/metro-pricing", label: "Metros" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchUsed, setSearchUsed] = useState(false);
  useEffect(() => {
    if (searchOpen) setSearchUsed(true);
  }, [searchOpen]);
  const [statePickerOpen, setStatePickerOpen] = useState(false);

  // Keyboard shortcut: ⌘K (mac) or Ctrl+K (win/linux) opens global search from anywhere
  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header
      className="sticky top-0 z-40 w-full border-b border-slate-200 bg-white/85 backdrop-blur-xl"
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-900 text-white">
            <Home className="h-5 w-5" />
          </span>
          <span className="font-display text-lg font-semibold tracking-tight text-slate-900">
            HavenCostGuide
          </span>
        </Link>

        <nav className="hidden items-center gap-7 md:flex">
          <DropdownMenu>
            <DropdownMenuTrigger
              className="inline-flex items-center gap-1 text-sm text-slate-600 outline-none transition-colors hover:text-slate-900 data-[state=open]:text-slate-900"
              data-testid="nav-calculators-trigger"
            >
              Calculators
              <ChevronDown className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-64 rounded-xl border border-slate-200 bg-white p-1 shadow-lg"
              data-testid="nav-calculators-menu"
            >
              <DropdownMenuItem asChild>
                <Link
                  to="/decision-tools"
                  className="cursor-pointer rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800 focus:bg-slate-800"
                  data-testid="nav-calculators-decision-hub"
                >
                  🎯 Decision tools hub →
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to="/calculators"
                  className="cursor-pointer rounded-lg px-3 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50 focus:bg-slate-50"
                  data-testid="nav-calculators-all"
                >
                  See all {CALCULATOR_LIST.length} calculators →
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to="/comfort-upgrade-estimator"
                  className="cursor-pointer rounded-lg bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-100 focus:bg-emerald-100"
                  data-testid="nav-calculators-comfort-bundle"
                >
                  🔋 Comfort upgrade bundle (HVAC + insulation) →
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to="/storm-damage-claim-calculator"
                  className="cursor-pointer rounded-lg px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 focus:bg-emerald-50"
                  data-testid="nav-calculators-storm-claim"
                >
                  ⚡ Should I file my storm claim? →
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to="/repair-or-replace-roof-calculator"
                  className="cursor-pointer rounded-lg px-3 py-2 text-sm font-semibold text-sky-700 hover:bg-sky-50 focus:bg-sky-50"
                  data-testid="nav-calculators-roof-decision"
                >
                  🛠️ Should I repair or replace my roof? →
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to="/diy-or-contractor-calculator"
                  className="cursor-pointer rounded-lg px-3 py-2 text-sm font-semibold text-violet-700 hover:bg-violet-50 focus:bg-violet-50"
                  data-testid="nav-calculators-diy-decision"
                >
                  🔨 Should I DIY or hire a contractor? →
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to="/renovation-roi-calculator"
                  className="cursor-pointer rounded-lg px-3 py-2 text-sm font-semibold text-teal-700 hover:bg-teal-50 focus:bg-teal-50"
                  data-testid="nav-calculators-roi-decision"
                >
                  📈 Will this reno increase my home value? →
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to="/renovation-financing-calculator"
                  className="cursor-pointer rounded-lg px-3 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-50 focus:bg-amber-50"
                  data-testid="nav-calculators-financing-decision"
                >
                  💰 How should I finance my renovation? →
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to="/home-equity-loan-calculator"
                  className="cursor-pointer rounded-lg px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 focus:bg-emerald-50"
                  data-testid="nav-calculators-home-equity-loan"
                >
                  🏦 Home equity loan payment + DTI check →
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to="/should-i-finish-my-basement-calculator"
                  className="cursor-pointer rounded-lg px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 focus:bg-indigo-50"
                  data-testid="nav-calculators-basement-finish-decision"
                >
                  🏠 Should I finish my basement? →
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to="/renovate-or-move-calculator"
                  className="cursor-pointer rounded-lg px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 focus:bg-indigo-50"
                  data-testid="nav-calculators-renovate-or-move"
                >
                  🔁 Renovate or move? (5/10/20-yr math) →
                </Link>
              </DropdownMenuItem>
              {/* 2026-02 — &ldquo;Helpful tools&rdquo; cluster surfacing the 4 highest-intent
                  hiring/decision tools at the bottom of the Calculators dropdown
                  so first-time visitors can reach them in 2 clicks from any
                  page. Pre-Bid Scope Checklist + Quote Fairness are the 2 newly
                  surfaced tools; Quote Comparison was already wired. Visually
                  separated by an in-menu label for scanability. */}
              <div className="my-1 border-t border-slate-100" aria-hidden="true" />
              <div className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                Helpful tools
              </div>
              <DropdownMenuItem asChild>
                <Link
                  to="/pre-bid-scope-checklist"
                  className="cursor-pointer rounded-lg bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-100 focus:bg-emerald-100"
                  data-testid="nav-calculators-pre-bid-scope"
                >
                  📝 Pre-bid scope checklist (before bidding) →
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to="/quote-fairness-calculator"
                  className="cursor-pointer rounded-lg px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 focus:bg-emerald-50"
                  data-testid="nav-calculators-quote-fairness"
                >
                  ⚖️ Is my contractor quote fair? →
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to="/contractor-quote-comparison"
                  className="cursor-pointer rounded-lg px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 focus:bg-emerald-50"
                  data-testid="nav-calculators-quote-comparison"
                >
                  📋 Compare 2-5 contractor quotes side by side →
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to="/how-much-to-spend-fixing-up-before-listing-calculator"
                  className="cursor-pointer rounded-lg px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 focus:bg-emerald-50"
                  data-testid="nav-calculators-pre-listing-spend"
                >
                  🏡 How much to spend before listing? →
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setStatePickerOpen(true);
                }}
                className="cursor-pointer rounded-lg px-3 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50 focus:bg-slate-50"
                data-testid="nav-calculators-by-state"
              >
                By state — jump straight in →
              </DropdownMenuItem>
              <div className="my-1 border-t border-slate-100" aria-hidden="true" />
              {CALCULATOR_LIST.map((c) => (
                <DropdownMenuItem key={c.slug} asChild>
                  <Link
                    to={c.path}
                    className="cursor-pointer rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900 focus:bg-slate-50"
                    data-testid={`nav-calculator-${c.slug}`}
                  >
                    {c.name}
                  </Link>
                </DropdownMenuItem>
              ))}
              {/* Partner-facing entry point at the very bottom of the
                  Calculators dropdown — partners (contractors, bloggers)
                  scanning the calculator menu also see the embed program. */}
              <div className="my-1 border-t border-slate-100" aria-hidden="true" />
              <DropdownMenuItem asChild>
                <Link
                  to="/embeds"
                  className="cursor-pointer rounded-lg px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 focus:bg-emerald-50"
                  data-testid="nav-embeds"
                >
                  Embed our calculators (free) →
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {/* Research clusters dropdown — surfaces the 6 SEO clusters
              (quote fairness, timeline, licensing, deposits, rebates, permit
              cost) plus the /state-overview aggregator. Previously these
              pages were only discoverable via sitemap / cross-cluster
              widgets / direct URL. */}
          <DropdownMenu>
            <DropdownMenuTrigger
              className="inline-flex items-center gap-1 text-sm text-slate-600 outline-none transition-colors hover:text-slate-900 data-[state=open]:text-slate-900"
              data-testid="nav-research-trigger"
            >
              Research
              <ChevronDown className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-72 rounded-xl border border-slate-200 bg-white p-1 shadow-lg"
              data-testid="nav-research-menu"
            >
              <DropdownMenuItem asChild>
                <Link
                  to={STATE_OVERVIEW_LINK.path}
                  className="cursor-pointer rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800 focus:bg-slate-800"
                  data-testid="nav-research-state-overview"
                >
                  {STATE_OVERVIEW_LINK.emoji} {STATE_OVERVIEW_LINK.shortLabel} →
                </Link>
              </DropdownMenuItem>
              <div className="my-1 border-t border-slate-100" aria-hidden="true" />
              {RESEARCH_CLUSTERS.map((c) => (
                <DropdownMenuItem key={c.slug} asChild>
                  <Link
                    to={c.path}
                    className="cursor-pointer rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900 focus:bg-slate-50"
                    data-testid={`nav-research-${c.slug}`}
                  >
                    <span className="mr-1">{c.emoji}</span> {c.label}
                  </Link>
                </DropdownMenuItem>
              ))}
              <div className="my-1 border-t border-slate-100" aria-hidden="true" />
              <p className="px-3 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                Featured research
              </p>
              {PILLAR_GUIDES.map((g) => (
                <DropdownMenuItem key={g.slug} asChild>
                  <Link
                    to={g.path}
                    className="cursor-pointer rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900 focus:bg-slate-50"
                    data-testid={`nav-research-pillar-${g.slug}`}
                  >
                    <span className="mr-1">{g.emoji}</span> {g.shortLabel}
                  </Link>
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem asChild>
                <Link
                  to="/guides/best-roi-renovations-2026-ranked"
                  className="cursor-pointer rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900 focus:bg-slate-50"
                  data-testid="nav-research-best-roi-renovations"
                >
                  <span className="mr-1">🏆</span> 10 best ROI renovations 2026
                </Link>
              </DropdownMenuItem>
              <div className="my-1 border-t border-slate-100" aria-hidden="true" />
              <p className="px-3 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                Free tools
              </p>
              {FREE_TOOLS.map((t) => (
                <DropdownMenuItem key={t.slug} asChild>
                  <Link
                    to={t.path}
                    className="cursor-pointer rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900 focus:bg-slate-50"
                    data-testid={`nav-free-tool-${t.slug}`}
                  >
                    <span className="mr-1">{t.emoji}</span> {t.label}
                  </Link>
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem asChild>
                <Link
                  to="/tools"
                  className="cursor-pointer rounded-lg px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 focus:bg-emerald-50"
                  data-testid="nav-tools-index"
                >
                  See all free tools →
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {otherLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `text-sm transition-colors ${
                  isActive
                    ? "font-medium text-slate-900"
                    : "text-slate-600 hover:text-slate-900"
                }`
              }
              data-testid={`nav-${l.label.toLowerCase()}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label="Search the site"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-slate-700 transition hover:bg-slate-100 md:h-9 md:w-auto md:gap-2 md:rounded-full md:border md:border-slate-200 md:bg-white md:px-3 md:text-sm md:text-slate-500"
            data-testid="header-search-button"
          >
            <Search className="h-4 w-4" />
            <span className="hidden md:inline">Search…</span>
            <kbd className="hidden rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 md:inline-block">
              ⌘K
            </kbd>
          </button>
          {/* 2026-09-29 pass 92v — Repointed from the lead-dialog popup to
              /calculators. Funnel data: header dialog opens converted 1/45
              (2%) — visitors clicking "quotes" bounce off a name/email wall,
              while results-page capture converts ~100%. Self-serve estimate
              first, lead capture after the number. */}
          <Link
            to="/calculators"
            className="hidden h-9 items-center rounded-full bg-green-600 px-5 text-sm font-medium text-white shadow-sm transition hover:bg-green-700 md:inline-flex"
            data-testid="header-get-quotes-button"
          >
            Get a Free Estimate
          </Link>
          <button
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-slate-700 md:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-slate-200 bg-white pb-24 md:hidden" data-testid="mobile-menu">
          <div className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-3">
            <p className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
              Calculators
            </p>
            <Link
              to="/calculators"
              onClick={() => setOpen(false)}
              className="rounded-md px-3 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-100"
              data-testid="mobile-nav-calculators-all"
            >
              See all {CALCULATOR_LIST.length} calculators →
            </Link>
            <Link
              to="/guides"
              onClick={() => setOpen(false)}
              className="rounded-md px-3 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-100"
              data-testid="mobile-nav-guides-top"
            >
              📚 Browse all renovation guides →
            </Link>
            <Link
              to="/comfort-upgrade-estimator"
              onClick={() => setOpen(false)}
              className="rounded-md bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-100"
              data-testid="mobile-nav-calculators-comfort-bundle"
            >
              🔋 Comfort upgrade bundle →
            </Link>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setStatePickerOpen(true);
              }}
              className="rounded-md px-3 py-2 text-left text-sm font-semibold text-slate-900 hover:bg-slate-100"
              data-testid="mobile-nav-calculators-by-state"
            >
              By state — jump straight in →
            </button>
            <Link
              to="/home-equity-loan-calculator"
              onClick={() => setOpen(false)}
              className="rounded-md px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"
              data-testid="mobile-nav-home-equity-loan"
            >
              🏦 Home equity loan calculator →
            </Link>
            {/* 2026-02 — Mobile parity for the desktop &ldquo;Helpful tools&rdquo;
                cluster: pre-bid scope, quote fairness, quote comparison. */}
            <div className="px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              Helpful tools
            </div>
            <Link
              to="/pre-bid-scope-checklist"
              onClick={() => setOpen(false)}
              className="rounded-md bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-100"
              data-testid="mobile-nav-pre-bid-scope"
            >
              📝 Pre-bid scope checklist →
            </Link>
            <Link
              to="/quote-fairness-calculator"
              onClick={() => setOpen(false)}
              className="rounded-md px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"
              data-testid="mobile-nav-quote-fairness"
            >
              ⚖️ Is my contractor quote fair? →
            </Link>
            <Link
              to="/contractor-quote-comparison"
              onClick={() => setOpen(false)}
              className="rounded-md px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"
              data-testid="mobile-nav-quote-comparison"
            >
              📋 Compare 2-5 contractor quotes →
            </Link>
            <Link
              to="/how-much-to-spend-fixing-up-before-listing-calculator"
              onClick={() => setOpen(false)}
              className="rounded-md px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"
              data-testid="mobile-nav-pre-listing-spend"
            >
              🏡 How much to spend before listing? →
            </Link>
            {CALCULATOR_LIST.map((c) => (
              <Link
                key={c.slug}
                to={c.path}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
                data-testid={`mobile-nav-calculator-${c.slug}`}
              >
                {c.name}
              </Link>
            ))}
            <div className="my-2 border-t border-slate-100" />
            <p className="px-3 pt-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
              Research clusters
            </p>
            <Link
              to={STATE_OVERVIEW_LINK.path}
              onClick={() => setOpen(false)}
              className="rounded-md px-3 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-100"
              data-testid="mobile-nav-research-state-overview"
            >
              {STATE_OVERVIEW_LINK.emoji} {STATE_OVERVIEW_LINK.shortLabel}
            </Link>
            {RESEARCH_CLUSTERS.map((c) => (
              <Link
                key={c.slug}
                to={c.path}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
                data-testid={`mobile-nav-research-${c.slug}`}
              >
                {c.emoji} {c.shortLabel}
              </Link>
            ))}
            <div className="my-2 border-t border-slate-100" />
            <p className="px-3 pt-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
              Featured research
            </p>
            {PILLAR_GUIDES.map((g) => (
              <Link
                key={g.slug}
                to={g.path}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
                data-testid={`mobile-nav-pillar-${g.slug}`}
              >
                {g.emoji} {g.shortLabel}
              </Link>
            ))}
            <div className="my-2 border-t border-slate-100" />
            <p className="px-3 pt-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
              Free tools
            </p>
            {FREE_TOOLS.map((t) => (
              <Link
                key={t.slug}
                to={t.path}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
                data-testid={`mobile-nav-free-tool-${t.slug}`}
              >
                {t.emoji} {t.shortLabel}
              </Link>
            ))}
            <Link
              to="/tools"
              onClick={() => setOpen(false)}
              className="rounded-md px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
              data-testid="mobile-nav-tools-index"
            >
              See all free tools →
            </Link>
            <Link
              to="/embeds"
              onClick={() => setOpen(false)}
              className="rounded-md px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
              data-testid="mobile-nav-embeds"
            >
              Embed our calculators (free) →
            </Link>
            <div className="my-2 border-t border-slate-100" />
            {otherLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/calculators"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex h-9 items-center justify-center rounded-full bg-green-600 px-4 text-sm font-medium text-white transition hover:bg-green-700"
              data-testid="mobile-get-quotes-button"
            >
              Get a Free Estimate
            </Link>
          </div>
        </div>
      )}

      {searchUsed && (
        <Suspense fallback={null}>
          <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
        </Suspense>
      )}
      <StatePickerDialog open={statePickerOpen} onOpenChange={setStatePickerOpen} />
    </header>
  );
}
